import { useState, useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { categories, kaspreadsheetUrl } from '@/mocks/categories';

export default function Navbar() {
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [mobileDropdownOpen, setMobileDropdownOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 20);
    };
    const handleClickOutside = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setDropdownOpen(false);
      }
    };
    window.addEventListener('scroll', handleScroll);
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      window.removeEventListener('scroll', handleScroll);
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const textColor = scrolled ? 'text-foreground-900' : 'text-background-50';
  const mutedTextColor = scrolled ? 'text-foreground-600' : 'text-background-50/85';
  const hoverTextColor = scrolled ? 'hover:text-primary-600' : 'hover:text-background-50';
  const dropdownBtnBg = scrolled
    ? 'bg-secondary-100 text-foreground-700 hover:bg-secondary-200'
    : 'bg-background-50/20 backdrop-blur-sm text-background-50 hover:bg-background-50/30 border border-background-50/20';
  const mobileBg = scrolled
    ? 'bg-background-50 border-background-200/60'
    : 'bg-background-50/95 backdrop-blur-md border-background-200/40';

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled
          ? 'bg-background-50/95 backdrop-blur-md border-b border-background-200/60'
          : 'bg-transparent'
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 md:px-6">
        <div className="flex items-center justify-between h-16">
          {/* Logo / Home */}
          <Link
            to="/"
            className={`flex items-center gap-2 transition-colors ${textColor} ${hoverTextColor}`}
          >
            <div className="w-8 h-8 flex items-center justify-center">
              <i className={`ri-grid-line text-xl ${scrolled ? 'text-primary-500' : 'text-background-50'}`}></i>
            </div>
            <span className="font-heading text-lg font-semibold whitespace-nowrap">
              BaseTao Spreadsheet
            </span>
          </Link>

          {/* Desktop Nav */}
          <div className="hidden md:flex items-center gap-5">
            {/* Home button */}
            <Link
              to="/"
              className={`text-sm transition-colors whitespace-nowrap ${mutedTextColor} ${hoverTextColor}`}
            >
              Home
            </Link>

            {/* Shopping Guide */}
            <Link
              to="/basetao-shopping-guide"
              className={`text-sm transition-colors whitespace-nowrap ${mutedTextColor} ${hoverTextColor}`}
            >
              Shopping Guide
            </Link>

            <Link
              to="/basetao-spreadsheet-guide"
              className={`text-sm transition-colors whitespace-nowrap ${mutedTextColor} ${hoverTextColor}`}
            >
              Complete Guide
            </Link>

            <a
              href={kaspreadsheetUrl}
              target="_blank"
              rel="noopener nofollow"
              className={`text-sm transition-colors whitespace-nowrap ${mutedTextColor} ${hoverTextColor}`}
            >
              Main Store
            </a>

            {/* Shop Categories Dropdown */}
            <div ref={dropdownRef} className="relative">
              <button
                onClick={() => setDropdownOpen(!dropdownOpen)}
                className={`flex items-center gap-1.5 px-4 py-2 rounded-full text-sm transition-colors whitespace-nowrap cursor-pointer ${dropdownBtnBg}`}
              >
                <span>Shop Categories</span>
                <div className="w-4 h-4 flex items-center justify-center">
                  <i className={`ri-arrow-down-s-line text-sm transition-transform duration-200 ${dropdownOpen ? 'rotate-180' : ''}`}></i>
                </div>
              </button>

              {dropdownOpen && (
                <div className="absolute right-0 mt-2 w-72 bg-background-50 rounded-lg border border-background-200/70 py-2 max-h-96 overflow-y-auto shadow-sm">
                  {categories.map((cat) => (
                    <a
                      key={cat.name}
                      href={cat.url}
                      target="_blank"
                      rel="noopener nofollow"
                      className="flex items-center gap-3 px-4 py-3 text-sm text-foreground-700 hover:bg-primary-50 hover:text-primary-700 transition-colors cursor-pointer"
                      onClick={() => setDropdownOpen(false)}
                    >
                      <div className="w-10 h-10 flex-shrink-0 rounded-md overflow-hidden">
                        <img
                          src={cat.image}
                          alt={cat.name}
                          className="w-full h-full object-cover"
                        />
                      </div>
                      <div className="flex items-center gap-2">
                        <div className="w-4 h-4 flex items-center justify-center">
                          <i className={`${cat.icon} text-xs text-primary-500`}></i>
                        </div>
                        <span className="whitespace-nowrap">{cat.name}</span>
                      </div>
                    </a>
                  ))}
                </div>
              )}
            </div>

            {/* Buy It Now button */}
            <a
              href={kaspreadsheetUrl}
              target="_blank"
              rel="noopener nofollow"
              className="px-5 py-2 rounded-full bg-accent-500 text-background-50 text-sm font-medium hover:bg-accent-600 transition-colors whitespace-nowrap cursor-pointer"
            >
              Buy It Now
            </a>
          </div>

          {/* Mobile hamburger */}
          <button
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            className={`md:hidden flex items-center justify-center w-10 h-10 cursor-pointer ${textColor}`}
          >
            <i className={`text-xl ${mobileMenuOpen ? 'ri-close-line' : 'ri-menu-line'}`}></i>
          </button>
        </div>

        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <div className={`md:hidden border-t py-4 max-h-[calc(100vh-4rem)] overflow-y-auto overscroll-contain ${mobileBg}`}>
            <div className="space-y-1">
              <Link
                to="/"
                onClick={() => setMobileMenuOpen(false)}
                className="flex items-center gap-3 px-4 py-3 text-sm text-foreground-700 hover:bg-background-100 rounded-lg transition-colors"
              >
                <div className="w-5 h-5 flex items-center justify-center">
                  <i className="ri-home-line text-sm text-primary-500"></i>
                </div>
                Home
              </Link>

              <Link
                to="/basetao-shopping-guide"
                onClick={() => setMobileMenuOpen(false)}
                className="flex items-center gap-3 px-4 py-3 text-sm text-foreground-700 hover:bg-background-100 rounded-lg transition-colors"
              >
                <div className="w-5 h-5 flex items-center justify-center">
                  <i className="ri-guide-line text-sm text-primary-500"></i>
                </div>
                Shopping Guide
              </Link>

              <Link
                to="/basetao-spreadsheet-guide"
                onClick={() => setMobileMenuOpen(false)}
                className="flex items-center gap-3 px-4 py-3 text-sm text-foreground-700 hover:bg-background-100 rounded-lg transition-colors"
              >
                <div className="w-5 h-5 flex items-center justify-center">
                  <i className="ri-book-open-line text-sm text-primary-500"></i>
                </div>
                Complete Guide
              </Link>

              <a
                href={kaspreadsheetUrl}
                target="_blank"
                rel="noopener nofollow"
                className="flex items-center gap-3 px-4 py-3 text-sm text-foreground-700 hover:bg-background-100 rounded-lg transition-colors"
              >
                <div className="w-5 h-5 flex items-center justify-center">
                  <i className="ri-store-line text-sm text-primary-500"></i>
                </div>
                Main Store
              </a>

              {/* Mobile Categories */}
              <button
                onClick={() => setMobileDropdownOpen(!mobileDropdownOpen)}
                className="w-full flex items-center justify-between px-4 py-3 text-sm text-foreground-700 hover:bg-background-100 rounded-lg transition-colors cursor-pointer"
              >
                <div className="flex items-center gap-3">
                  <div className="w-5 h-5 flex items-center justify-center">
                    <i className="ri-grid-line text-sm text-primary-500"></i>
                  </div>
                  <span className="font-medium">Shop Categories</span>
                </div>
                <div className="w-4 h-4 flex items-center justify-center">
                  <i className={`ri-arrow-down-s-line text-sm transition-transform duration-200 ${mobileDropdownOpen ? 'rotate-180' : ''}`}></i>
                </div>
              </button>

              {mobileDropdownOpen && (
                <div className="grid grid-cols-2 gap-1 px-4 pb-2">
                  {categories.map((cat) => (
                    <a
                      key={cat.name}
                      href={cat.url}
                      target="_blank"
                      rel="noopener nofollow"
                      className="flex items-center gap-2 px-2 py-2.5 text-xs text-foreground-600 hover:bg-primary-50 hover:text-primary-700 rounded-lg transition-colors cursor-pointer"
                      onClick={() => { setMobileDropdownOpen(false); setMobileMenuOpen(false); }}
                    >
                      <div className="w-8 h-8 flex-shrink-0 rounded-md overflow-hidden">
                        <img
                          src={cat.image}
                          alt={cat.name}
                          className="w-full h-full object-cover"
                        />
                      </div>
                      <span className="leading-tight">{cat.name}</span>
                    </a>
                  ))}
                </div>
              )}

              <a
                href={kaspreadsheetUrl}
                target="_blank"
                rel="noopener nofollow"
                className="block mx-4 mt-3 px-5 py-2.5 rounded-full bg-accent-500 text-background-50 text-sm font-medium text-center hover:bg-accent-600 transition-colors cursor-pointer"
                onClick={() => setMobileMenuOpen(false)}
              >
                Buy It Now
              </a>
            </div>
          </div>
        )}
      </div>
    </nav>
  );
}